import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Header from "../components/app/Header";
import { toast } from "sonner";
import {
  Check,
  X,
  Loader2,
  Sparkles,
  Quote as QuoteIcon,
  Inbox,
  CheckCheck,
} from "lucide-react";

export default function FieldReview() {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState({}); // fill_id -> "accept"|"reject"|"bulk"

  const load = useCallback(async () => {
    try {
      const r = await api.get("/field-fills/review");
      setGroups(r.data?.groups || []);
    } catch (_e) {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setBusyFor = (key, value) => setBusy((b) => ({ ...b, [key]: value }));

  const accept = async (fillId) => {
    setBusyFor(fillId, "accept");
    try {
      await api.post(`/field-fills/${fillId}/accept`);
      toast.success("Written to GHL.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed.");
    } finally {
      setBusyFor(fillId, null);
    }
  };

  const reject = async (fillId) => {
    setBusyFor(fillId, "reject");
    try {
      await api.post(`/field-fills/${fillId}/reject`);
      toast.success("Rejected.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed.");
    } finally {
      setBusyFor(fillId, null);
    }
  };

  const acceptAll = async (contactId) => {
    setBusyFor(contactId, "bulk");
    try {
      const r = await api.post(`/field-fills/accept-contact/${contactId}`);
      toast.success(`Wrote ${r.data?.accepted || 0} fields.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch accept failed.");
    } finally {
      setBusyFor(contactId, null);
    }
  };

  const totalPending = (groups || []).reduce(
    (n, g) => n + (g.fills?.length || 0),
    0
  );

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-16">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400">
            // Employz Intelligence / Field Review
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tighter font-black text-white flex items-center gap-3">
            <Sparkles className="w-9 h-9 text-emerald-400" />
            Field Review
          </h1>
          <p className="mt-2 text-zinc-500 text-sm max-w-2xl">
            Low-confidence CRM fields our AI pulled from customer conversations.
            Accept to write to GoHighLevel, reject to discard. High-confidence
            fields are written automatically.
          </p>
        </div>

        <div
          className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-950/20 via-zinc-950 to-zinc-950 p-6 mb-6"
          data-testid="field-review-headline"
        >
          <div className="text-2xl md:text-3xl text-white font-display font-black tracking-tight">
            <span className="text-amber-300" data-testid="field-review-pending-count">
              {totalPending}
            </span>{" "}
            proposed field{totalPending === 1 ? "" : "s"} pending your review
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            Grouped by contact — accept-all writes every pending field in one call.
          </div>
        </div>

        {groups === null ? (
          <div className="text-sm text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </div>
        ) : groups.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-zinc-800 py-14 text-center text-sm text-zinc-500 flex flex-col items-center gap-2"
            data-testid="field-review-empty"
          >
            <Inbox className="w-6 h-6 text-zinc-700" />
            Nothing pending. High-confidence fills are written straight to GHL.
          </div>
        ) : (
          <div className="space-y-4" data-testid="field-review-groups">
            {groups.map((g) => (
              <div
                key={g.ghl_contact_id}
                data-testid={`field-review-group-${g.ghl_contact_id}`}
                className="rounded-xl border border-zinc-900 bg-zinc-950/60 p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="text-white font-medium">{g.contact_name}</div>
                  <button
                    onClick={() => acceptAll(g.ghl_contact_id)}
                    disabled={busy[g.ghl_contact_id] === "bulk"}
                    data-testid={`field-review-accept-all-${g.ghl_contact_id}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded"
                  >
                    {busy[g.ghl_contact_id] === "bulk" ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <CheckCheck className="w-3 h-3" />
                    )}
                    Accept all
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  {(g.fills || []).map((f) => (
                    <div
                      key={f.id}
                      data-testid={`field-review-row-${f.id}`}
                      className="rounded-lg border border-zinc-900 bg-zinc-950 p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white">
                            <span className="text-zinc-500">{f.field}</span>{" "}
                            <span className="text-zinc-700">=</span>{" "}
                            <span className="text-emerald-300 font-medium">
                              {f.value}
                            </span>
                          </div>
                          {f.evidence && (
                            <div className="mt-1 text-[11px] text-zinc-500 italic flex items-start gap-1">
                              <QuoteIcon className="w-3 h-3 mt-0.5 shrink-0" />
                              <span>&ldquo;{f.evidence}&rdquo;</span>
                            </div>
                          )}
                          <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-zinc-600">
                            from {f.source_agent?.replace(/_/g, " ")} · confidence {f.confidence}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => accept(f.id)}
                            disabled={!!busy[f.id]}
                            data-testid={`field-review-accept-${f.id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded"
                          >
                            {busy[f.id] === "accept" ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )}
                            Accept
                          </button>
                          <button
                            onClick={() => reject(f.id)}
                            disabled={!!busy[f.id]}
                            data-testid={`field-review-reject-${f.id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-zinc-800 text-zinc-500 rounded"
                          >
                            {busy[f.id] === "reject" ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
